/**
 * Services Component
 * 
 * Displays comprehensive range of logistics services in gradient card grid.
 * 
 * DESIGN STANDARDS:
 * - 3-column responsive grid (lg:3, md:2, mobile:1)
 * - Gradient cards matching About page "Achievements" style
 * - Consistent spacing: py-20 section padding
 * - Icon size: w-10 h-10 with accent background and glow effect
 * - Scroll-triggered animations with staggered delays
 * - Accessible with proper ARIA labels and semantic HTML
 * 
 * Services Include:
 * - Port & Ship Agency
 * - Ship Chartering
 * - Project Cargo & Consulting
 * - Rail Logistics
 * - Road Transport
 * - Air Freight
 * 
 * @component
 */

import { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Ship, Plane, Train, Truck, Boxes, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { SectionHeader } from "@/components/common/SectionHeader";

/** Service configuration with icon, content, and navigation */
const services = [
  {
    icon: Ship,
    title: "Port & Ship Agency",
    description: "Comprehensive port operations and ship agency services with expert handling of vessel needs and documentation.",
    link: "/services/port-ship-agency",
  },
  {
    icon: Boxes,
    title: "Ship Chartering",
    description: "Worldwide vessel chartering services for all types of cargo with flexible solutions tailored to your needs.",
    link: "/services/ship-chartering",
  },
  {
    icon: FileCheck,
    title: "Project Cargo & Consulting",
    description: "Expert handling of out-of-gauge shipments, complex projects, and strategic logistics consulting.",
    link: "/services/project-cargo",
  },
  {
    icon: Train,
    title: "Rail & Road Logistics",
    description: "Integrated rail and road transport solutions combining efficiency, flexibility, and environmental responsibility for comprehensive supply chain coverage.",
    link: "/services/rail-road-logistics",
  },
  {
    icon: Truck,
    title: "Terminal & Port Development",
    description: "Comprehensive port infrastructure development from terminal design to operational management, including EU-funded projects and facility modernization.",
    link: "/services/terminal-port-development",
  },
  {
    icon: Plane,
    title: "Air Freight",
    description: "Time-critical air cargo solutions with priority handling and customs clearance support.",
    link: "/services/air-freight",
  },
];

const Services = () => {
  const navigate = useNavigate();
  const { ref, isVisible } = useScrollAnimation();
  
  const handleServiceClick = useCallback((link: string) => {
    navigate(link);
  }, [navigate]);
  
  return (
    <section 
      id="services" 
      className="py-20 bg-background" 
      ref={ref} 
      aria-labelledby="services-heading"
    >
        <div className="container mx-auto px-6">
          
          {/* Section Header */}
          <div 
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <SectionHeader
              eyebrow="Our Services"
              title="Complete Logistics Solutions"
              description="From quay to final delivery, we provide end-to-end transport services across all modes with seamless coordination."
            />
          </div>

          {/* Service Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <article
                  key={index}
                  className={`group p-6 md:p-8 rounded-xl bg-gradient-to-br from-accent/5 to-transparent border border-border/50 hover:border-accent/30 card-hover transition-all duration-300 flex flex-col ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: isVisible ? `${index * 100}ms` : '0ms' }}
                  aria-labelledby={`service-${index}-title`}
                >
                  {/* Icon with glow effect */}
                  <div className="mb-6 inline-flex p-4 rounded-xl bg-accent/10 text-accent icon-hover group-hover:shadow-accent/20 group-hover:shadow-lg w-fit" aria-hidden="true">
                    <Icon className="w-10 h-10" strokeWidth={1.5} />
                  </div>
                  
                  {/* Content */}
                  <h3 
                    id={`service-${index}-title`}
                    className="text-xl font-semibold text-foreground mb-3 font-newsreader"
                  >
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-6 flex-grow">
                    {service.description}
                  </p>
                  
                  {/* CTA Button */}
                  <Button 
                    variant="outline"
                    className="group/btn w-full justify-center mt-auto"
                    onClick={() => handleServiceClick(service.link)}
                    aria-label={`Learn more about ${service.title}`}
                  >
                    <span>View Details</span>
                  </Button>
                </article>
              );
            })}
          </div>
        </div>
      </section>
  );
};

export default memo(Services);
